import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { DecisionState, DecisionSummary } from "../../../models/home";
import { LanguageService } from "../../../services/language/language.service";
import { UxError } from "../../../models/ux-error.model";

type DecisionStateFilter = DecisionState | "all" | "attention";

interface SummaryItem {
  state: DecisionStateFilter;
  count: number;
  iconClass: string;
}

@Component({
  selector: "app-home-decision-summary",
  templateUrl: "./home-decision-summary.component.html",
  styleUrls: ["./home-decision-summary.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDecisionSummaryComponent {
  @Input() summary: DecisionSummary | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() activeState: DecisionStateFilter = "all";
  @Output() stateSelect = new EventEmitter<DecisionStateFilter>();

  constructor(public readonly lang: LanguageService) {}

  get items(): readonly SummaryItem[] {
    const summary = this.summary;

    return [
      { state: "act_now", count: summary?.actNow ?? 0, iconClass: this.getStateIconClass("act_now") },
      { state: "act_soon", count: summary?.actSoon ?? 0, iconClass: this.getStateIconClass("act_soon") },
      { state: "waiting", count: summary?.waiting ?? 0, iconClass: this.getStateIconClass("waiting") },
      {
        state: "act_without_rush",
        count: summary?.actWithoutRush ?? summary?.safeToIgnore ?? 0,
        iconClass: this.getStateIconClass("act_without_rush")
      },
      { state: "completed", count: summary?.completed ?? 0, iconClass: this.getStateIconClass("completed") }
    ];
  }

  onSelect(state: DecisionStateFilter): void {
    this.stateSelect.emit(state);
  }

  getLabel(state: DecisionStateFilter): string {
    return this.lang.translate(`home.decisionStates.${state}`);
  }

  private getStateIconClass(state: DecisionState): string {
    switch (state) {
      case "act_now":
        return "fa-bolt";
      case "act_soon":
        return "fa-clock";
      case "waiting":
        return "fa-pause-circle";
      case "completed":
        return "fa-check-circle";
      case "act_without_rush":
      default:
        return "fa-info-circle";
    }
  }
}